import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useIssueMemory, useIssueDossier, useRelatedIssues, useIssueHistory, useSetAcceptance, useSetGoal, useAddComment, useAssignable } from '@/hooks/useIssueMemory';
import { IssueMemoryRecord } from '@/api/issue-memory.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Markdown } from '@/components/common/Markdown';
import { AssignPicker, useCanAssign } from '@/components/common/AssignPicker';
import { Input } from '@/components/ui/input';
import { ArrowLeft, CheckCircle2, Circle, ExternalLink, Pencil, Plus, Send, X, XCircle } from 'lucide-react';
import { apiErrorMessage, cn, formatDate, formatDateTime, formatRelativeTime } from '@/utils/helpers';
import { useAuth } from '@/context/AuthContext';
import { useAgentActions, useAgentProfiles } from '@/hooks/useAgents';

/**
 * One issue's memory: what the team and the agents know about it (plan §4.5, §9).
 */

type Tab = 'overview' | 'agents' | 'history';

// AI SDLC branch names: <type>/<iid>-<slug>
const ISSUE_BRANCH = /^(?:feat|feature|fix|bug|hotfix|security|devops|chore|refactor|docs|test|perf)\/(\d+)(?:-|$)/i;

function issueBranchIid(branch?: string): number | null {
  const match = branch ? ISSUE_BRANCH.exec(branch) : null;
  return match ? parseInt(match[1], 10) : null;
}

function Section({ title, children, empty, action }: { title: string; children?: React.ReactNode; empty?: boolean; action?: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent className="text-sm">{empty ? <p className="text-muted-foreground">None</p> : children}</CardContent>
    </Card>
  );
}

function SourceLink({ url, label = 'source' }: { url?: string; label?: string }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-primary hover:underline">
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

/** The AI developer owns the issue and waits for information before it starts (its readiness questions) */
function useAiIsWaiting(mem: IssueMemoryRecord, enabled = true): boolean {
  const { data: members } = useAssignable(String(mem.gitlabProjectId), String(mem.iid), enabled);
  const ai = members?.find((m) => m.ai)?.username;
  return !!ai && mem.status === 'needs-info' && mem.assignees.includes(ai);
}

/** What a description edit did, in a few words */
function savedNote(outcome: { decision: string; denyReason?: string }, aiWaiting: boolean): string {
  if (outcome.decision === 'executed') {
    return aiWaiting ? 'Saved in GitLab. The AI developer was waiting for this and starts now.' : 'Saved in the issue description in GitLab.';
  }
  if (outcome.decision === 'unchanged') return 'No change.';
  if (outcome.decision === 'shadowed') return 'Recorded only: this project is in shadow mode.';
  return `Not saved: ${outcome.denyReason || outcome.decision}`;
}

/** The goal is the description's "What" section or first paragraph; editing it changes only that part in GitLab */
function Goal({ mem }: { mem: IssueMemoryRecord }) {
  const canEdit = useCanAssign(mem.state === 'opened');
  const save = useSetGoal(String(mem.gitlabProjectId), String(mem.iid));
  const aiWaiting = useAiIsWaiting(mem, canEdit);
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const submit = async () => {
    try {
      const outcome = await save.mutateAsync(draft!.trim());
      setDraft(null);
      setNote(savedNote(outcome, aiWaiting));
    } catch (e) {
      setNote(apiErrorMessage(e));
    }
  };

  return (
    <Section
      title="Goal"
      empty={!draft && !mem.goal && !mem.summary && !note}
      action={
        canEdit && draft === null ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNote(null);
              setDraft(mem.goal || '');
            }}
          >
            <Pencil className="mr-1 h-3 w-3" /> Edit
          </Button>
        ) : undefined
      }
    >
      {draft !== null ? (
        <div className="space-y-2">
          <textarea
            dir="auto"
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="What should change, and where (app, page, endpoint)? One or two sentences on the expected result."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={draft.trim().length < 20 || save.isPending} onClick={submit}>
              Save to GitLab
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <span className="text-xs text-muted-foreground">
              {draft.trim().length < 20 ? 'At least 20 characters. ' : ''}Replaces the description's goal paragraph; the rest stays as it is.
            </span>
          </div>
        </div>
      ) : (
        mem.goal && <Markdown text={mem.goal} />
      )}
      {mem.summary && (
        <Markdown text={mem.summary} className="mt-2 text-muted-foreground" />
      )}
      {mem.howToTest && (
        <p className="mt-2">
          <span className="font-medium">How to test:</span> {mem.howToTest}
        </p>
      )}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </Section>
  );
}

/**
 * Executives and PMs edit the criteria here: they are written into the description's "Acceptance
 * criteria" section in GitLab (the template), so the issue stays the source and the rest of it is kept.
 */
function Acceptance({ mem }: { mem: IssueMemoryRecord }) {
  const canEdit = useCanAssign(mem.state === 'opened');
  const save = useSetAcceptance(String(mem.gitlabProjectId), String(mem.iid));
  const aiWaiting = useAiIsWaiting(mem, canEdit);
  const [draft, setDraft] = useState<Array<{ text: string; done: boolean }> | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const edit = () => {
    setNote(null);
    setDraft(mem.acceptance.length ? mem.acceptance.map((a) => ({ text: a.text, done: a.done })) : [{ text: '', done: false }]);
  };
  const change = (i: number, item: Partial<{ text: string; done: boolean }>) =>
    setDraft((d) => d!.map((a, j) => (j === i ? { ...a, ...item } : a)));

  const submit = async () => {
    const items = draft!.map((a) => ({ text: a.text.trim(), done: a.done })).filter((a) => a.text);
    try {
      const outcome = await save.mutateAsync(items);
      setDraft(null);
      setNote(savedNote(outcome, aiWaiting));
    } catch (e) {
      setNote(apiErrorMessage(e));
    }
  };

  return (
    <Section
      title="Acceptance"
      empty={!draft && !mem.acceptance.length && !note}
      action={
        canEdit && !draft ? (
          <Button variant="ghost" size="sm" onClick={edit}>
            <Pencil className="mr-1 h-3 w-3" /> Edit
          </Button>
        ) : undefined
      }
    >
      {draft ? (
        <div className="space-y-2">
          {draft.map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="checkbox" checked={a.done} onChange={(e) => change(i, { done: e.target.checked })} title="Done" />
              <Input
                dir="auto"
                value={a.text}
                maxLength={160}
                placeholder='e.g. the page at / says "Welcome to AI assistant"'
                onChange={(e) => change(i, { text: e.target.value })}
              />
              <Button variant="ghost" size="icon" title="Remove" onClick={() => setDraft((d) => d!.filter((_, j) => j !== i))}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={draft.length >= 20} onClick={() => setDraft((d) => [...d!, { text: '', done: false }])}>
              <Plus className="mr-1 h-3 w-3" /> Add criterion
            </Button>
            <Button size="sm" disabled={save.isPending} onClick={submit}>
              Save to GitLab
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
              Cancel
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Saved in the description's "Acceptance criteria" section; the rest of the description stays as it is.
          </p>
        </div>
      ) : (
        <ul className="space-y-1">
          {mem.acceptance.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              {a.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
              <Markdown text={a.text} inline />
            </li>
          ))}
        </ul>
      )}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </Section>
  );
}

/** A comment in GitLab with your name; an answer to the AI developer's question starts its work */
function CommentBox({ mem }: { mem: IssueMemoryRecord }) {
  const add = useAddComment(String(mem.gitlabProjectId), String(mem.iid));
  const aiWaiting = useAiIsWaiting(mem);
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const submit = async () => {
    setNote(null);
    try {
      const outcome = await add.mutateAsync(text.trim());
      if (outcome.decision === 'executed') setText('');
      setNote(
        outcome.decision === 'executed'
          ? aiWaiting
            ? 'Posted. The AI developer takes it as your answer and starts.'
            : 'Posted in GitLab.'
          : outcome.decision === 'shadowed'
            ? 'Recorded only: this project is in shadow mode.'
            : `Not posted: ${outcome.denyReason || outcome.decision}`
      );
    } catch (e) {
      setNote(apiErrorMessage(e));
    }
  };

  return (
    <div className="mb-4 space-y-2">
      <textarea
        dir="auto"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={aiWaiting ? 'Answer the question above…' : 'Write a comment…'}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!text.trim() || add.isPending} onClick={submit}>
          <Send className="mr-1 h-3 w-3" /> Comment in GitLab
        </Button>
        {note && <span className="text-xs text-muted-foreground">{note}</span>}
      </div>
    </div>
  );
}

function Overview({ mem }: { mem: IssueMemoryRecord }) {
  const { project, iid } = useParams();
  const { data: related = [] } = useRelatedIssues(project, iid);
  const openQuestions = mem.questions.filter((q) => !q.answeredAt);
  const answered = mem.questions.filter((q) => q.answeredAt);
  const blockers = mem.blockers.filter((b) => !b.clearedAt);
  const mrBranches = new Set(mem.mrs.map((m) => `${m.gitlabProjectId}:${m.sourceBranch}`));
  const loneBranches = mem.branches.filter((b) => !mrBranches.has(`${b.gitlabProjectId}:${b.name}`));
  // The issue's own MRs (branch <type>/<iid>-...), or else the ones that close it; the rest only mention it
  const own = mem.mrs.filter((m) => issueBranchIid(m.sourceBranch) === mem.iid);
  const workMrs = own.length ? own : mem.mrs.filter((m) => m.closing);
  const otherMrs = mem.mrs.filter((m) => !workMrs.includes(m));
  const canComment = useCanAssign(mem.state === 'opened');

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Goal mem={mem} />

        <Acceptance mem={mem} />

        <Section title={`Decisions (${mem.decisions.length})`} empty={!mem.decisions.length}>
          <ul className="space-y-2">
            {[...mem.decisions].reverse().map((d, i) => (
              <li key={i}>
                <span className="text-muted-foreground">
                  {formatDateTime(d.at)}
                  {d.by ? ` · @${d.by}` : ''}:
                </span>{' '}
                <Markdown text={d.text} inline />
                <SourceLink url={d.sourceUrl} />
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Questions" empty={!mem.questions.length}>
          {openQuestions.map((q) => (
            <div key={q.qid} className="mb-2 rounded-md border border-amber-200 bg-amber-50 p-2">
              <span className="font-medium">{q.askedTo ? `@${q.askedTo}` : 'Anyone'}</span>
              <span className="text-muted-foreground"> · asked {formatRelativeTime(q.askedAt)}{q.askedBy ? ` by @${q.askedBy}` : ''}</span>
              <Markdown text={q.text} />
            </div>
          ))}
          {answered.map((q) => (
            <div key={q.qid} className="mb-2 text-muted-foreground">
              <p>
                <Markdown text={q.text} inline className="line-through" />
              </p>
              <p className="text-foreground">
                → <Markdown text={q.answer || ''} inline /> {q.answeredBy && <span className="text-muted-foreground">(@{q.answeredBy})</span>}
                <SourceLink url={q.answerUrl} />
              </p>
            </div>
          ))}
        </Section>

        <Section title="Recent comments" empty={!mem.recentComments?.length && !canComment}>
          {canComment && <CommentBox mem={mem} />}
          <ul className="space-y-2">
            {[...(mem.recentComments || [])].reverse().map((c, i) => (
              <li key={i}>
                <span className="text-muted-foreground">
                  {formatDateTime(c.at)}
                  {c.by ? ` · @${c.by}` : ''}
                  <SourceLink url={c.url} label="open" />
                </span>
                <Markdown text={c.text} />
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Progress" empty={!mem.progress.length}>
          <ul className="space-y-1">
            {[...mem.progress].reverse().map((p, i) => (
              <li key={i}>
                <span className="text-muted-foreground">
                  {formatDateTime(p.at)}
                  {p.by ? ` · @${p.by}` : ''}:
                </span>{' '}
                <Markdown text={p.text} inline />
              </li>
            ))}
          </ul>
        </Section>
      </div>

      <div className="space-y-4">
        {mem.dod && (
          <Section title={`Definition of done (${mem.dod.profile})`}>
            {mem.dod.override ? (
              <p className="text-amber-700">
                Overridden by @{mem.dod.override.by}: {mem.dod.override.reason}
              </p>
            ) : mem.dod.passed ? (
              <p className="text-green-700">Met when closed ({formatDate(mem.dod.checkedAt)})</p>
            ) : (
              <>
                <p className="text-red-700">Not met when closed ({formatDate(mem.dod.checkedAt)}{mem.dod.failures > 1 ? `, ${mem.dod.failures} times` : ''})</p>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {mem.dod.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </>
            )}
            {mem.dod.warnings.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{mem.dod.warnings.join(' · ')}</p>}
          </Section>
        )}

        <Section title="Blockers" empty={!blockers.length}>
          {blockers.map((b) => (
            <p key={b.bid} className="text-red-700">
              <Markdown text={b.text} inline /> <span className="text-muted-foreground">({formatRelativeTime(b.at)})</span>
            </p>
          ))}
        </Section>

        <Section title="Branches and MRs" empty={!mem.mrs.length && !loneBranches.length}>
          <ul className="space-y-2">
            {workMrs.map((m) => (
              <li key={`${m.gitlabProjectId}-${m.iid}`}>
                <a href={m.webUrl} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">
                  {m.gitlabProjectId === mem.gitlabProjectId ? '' : m.projectPath?.split('/').pop() || ''}!{m.iid}
                </a>{' '}
                <span className="text-muted-foreground">
                  {m.state === 'opened' && m.draft ? 'draft' : m.state}
                  {m.closing ? ' · closes the issue' : ''}
                </span>
                {m.sourceBranch && (
                  <div className="text-xs font-mono text-muted-foreground">
                    {m.sourceBranch} → {m.targetBranch}
                  </div>
                )}
                {m.pipeline?.status && (
                  <div className={cn('text-xs', m.pipeline.status === 'success' ? 'text-green-600' : m.pipeline.status === 'failed' ? 'text-red-600' : '')}>
                    pipeline {m.pipeline.status}
                  </div>
                )}
                {m.reviewApp?.status && (
                  <div className="text-xs">
                    review app {m.reviewApp.status}
                    {m.reviewApp.status === 'up' && m.reviewApp.url && <SourceLink url={m.reviewApp.url} label={m.reviewApp.url.replace(/^https?:\/\//, '')} />}
                  </div>
                )}
              </li>
            ))}
            {loneBranches.map((b) => (
              <li key={`${b.gitlabProjectId}-${b.name}`} className="text-xs font-mono text-muted-foreground">
                {b.projectPath ? `${b.projectPath.split('/').pop()}: ` : ''}
                {b.name} (no MR yet)
              </li>
            ))}
            {otherMrs.length > 0 && (
              <li className="text-xs text-muted-foreground">
                Also mentioned in{' '}
                {otherMrs.map((m, i) => (
                  <span key={`${m.gitlabProjectId}-${m.iid}`}>
                    {i > 0 && ', '}
                    <a href={m.webUrl} target="_blank" rel="noreferrer" className="hover:underline">
                      {m.gitlabProjectId === mem.gitlabProjectId ? '' : m.projectPath?.split('/').pop() || ''}!{m.iid}
                    </a>
                    {m.closing ? ' (closes)' : ''}
                  </span>
                ))}
              </li>
            )}
          </ul>
        </Section>

        <Section title="Evidence" empty={!mem.evidence.length}>
          <ul className="space-y-1">
            {[...mem.evidence].reverse().map((e, i) => (
              <li key={i} className="flex items-start gap-2">
                {e.verdict === 'pass' ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" /> : <XCircle className="mt-0.5 h-4 w-4 text-red-600" />}
                <span>
                  {e.mrIid ? `!${e.mrIid} ` : ''}
                  <Markdown text={e.summary} inline />
                  <span className="text-muted-foreground"> · {formatDateTime(e.at)}</span>
                  <SourceLink url={e.sourceUrl} label="comment" />
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title={`Files touched (${mem.filesTouched.length})`} empty={!mem.filesTouched.length}>
          <ul className="max-h-48 space-y-0.5 overflow-auto font-mono text-xs">
            {mem.filesTouched.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </Section>

        <Section title="Sessions" empty={!mem.sessions.length}>
          <ul className="space-y-2 text-xs">
            {[...mem.sessions].reverse().map((s) => (
              <li key={s.sessionId}>
                <div className="font-mono">{s.sessionId}</div>
                <div className="text-muted-foreground">
                  {s.agent || 'unknown'} · last active {s.lastActiveAt ? formatRelativeTime(s.lastActiveAt) : '?'}
                  {s.turns ? ` · ${s.turns} turns` : ''}
                  {s.costUsd ? ` · $${s.costUsd.toFixed(2)}` : ''}
                </div>
                {s.summary && <Markdown text={s.summary} />}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Related issues" empty={!related.length}>
          <ul className="space-y-1">
            {related.map((r) => (
              <li key={r.ref}>
                <span className="font-mono text-xs">{r.ref}</span> {r.title}
                <div className="text-xs text-muted-foreground">
                  {r.state} · {r.reasons.join(', ')}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
}

function AgentView() {
  const { project, iid } = useParams();
  const { data, isLoading } = useIssueDossier(project, iid, true);
  const [raw, setRaw] = useState(false);
  if (isLoading) return <LoadingSpinner className="py-12" />;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">What agents and Claude Code sessions receive</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setRaw(!raw)}>
            {raw ? 'Formatted' : 'Raw text'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{data ? `${data.length} characters` : ''}</p>
      </CardHeader>
      <CardContent>
        {raw ? (
          <pre dir="auto" className="whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-xs">
            {data}
          </pre>
        ) : (
          <Markdown text={data || ''} className="rounded-md bg-muted/40 p-4 text-sm" />
        )}
      </CardContent>
    </Card>
  );
}

function History() {
  const { project, iid } = useParams();
  const { data = [], isLoading } = useIssueHistory(project, iid);
  if (isLoading) return <LoadingSpinner className="py-12" />;
  return (
    <Card>
      <CardContent className="p-4">
        <ol className="space-y-3 text-sm">
          {data.map((h) => (
            <li key={h.revision} className="border-l-2 pl-3">
              <div className="text-xs text-muted-foreground">
                revision {h.revision} · {formatRelativeTime(h.at)}
                {h.by ? ` · ${h.by}` : ''}
              </div>
              <ul className="list-disc pl-4">
                {h.changes.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

/** Start the AI developer on this issue, or a review of its open MR (through the Action Gateway) */
function AiActions({ mem }: { mem: IssueMemoryRecord }) {
  const { user } = useAuth();
  const { dispatch } = useAgentActions();
  const { data: profiles } = useAgentProfiles();
  const [profile, setProfile] = useState('');
  const [note, setNote] = useState<string | null>(null);
  if (!['executive', 'pm'].includes(user?.role || '') || mem.state !== 'opened') return null;
  const project = String(mem.gitlabProjectId);
  const openMr = mem.mrs.find((m) => m.state === 'opened' && m.gitlabProjectId === mem.gitlabProjectId);

  const start = async (input: { role: 'developer' | 'reviewer'; iid?: number; mrIid?: number }) => {
    setNote(null);
    try {
      const outcome = await dispatch.mutateAsync({ project, ...input, profile: profile || undefined });
      setNote(
        outcome.decision === 'executed'
          ? 'Started; see AI sessions.'
          : outcome.decision === 'shadowed'
            ? 'Recorded only: this project is in shadow mode.'
            : `Not started: ${outcome.denyReason || outcome.decision}`
      );
    } catch (e) {
      setNote(apiErrorMessage(e));
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-2">
        {(profiles?.profiles.length || 0) > 1 && (
          <select
            className="h-8 rounded border bg-background px-2 text-xs"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            title="Which AI agent does the work (default: the issue's agent: label, then the project's default)"
          >
            <option value="">Agent: automatic</option>
            {profiles!.profiles.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}
                {p.billing === 'subscription' ? ' (subscription)' : ''}
              </option>
            ))}
          </select>
        )}
        <Button variant="outline" size="sm" disabled={dispatch.isPending} onClick={() => start({ role: 'developer', iid: mem.iid })}>
          Start AI developer
        </Button>
        {openMr && (
          <Button variant="outline" size="sm" disabled={dispatch.isPending} onClick={() => start({ role: 'reviewer', mrIid: openMr.iid })}>
            Review !{openMr.iid}
          </Button>
        )}
      </div>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}

export default function IssueMemoryPage() {
  const { project, iid } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const { data: mem, isLoading, error } = useIssueMemory(project, iid);

  if (isLoading) return <LoadingSpinner className="py-24" />;
  if (error || !mem) {
    return (
      <div className="space-y-4">
        <Link to="/board" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Board
        </Link>
        <p className="text-sm text-red-600">No memory for this issue yet.</p>
      </div>
    );
  }

  const ref = `${mem.projectPath?.split('/').pop() || mem.gitlabProjectId}#${mem.iid}`;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/board" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Board
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-sm text-muted-foreground">{ref}</p>
            <h1 dir="auto" className="text-2xl font-bold">
              {mem.title}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{mem.status}</span>
              {' · '}
              <AssignPicker gitlabProjectId={mem.gitlabProjectId} iid={mem.iid} assignees={mem.assignees} open={mem.state === 'opened'} />
              {mem.waitingOn?.username && <span className="text-amber-700"> · waiting on @{mem.waitingOn.username}</span>}
              {mem.milestone && ` · ${mem.milestone}`}
              {mem.lastHumanActivityAt && ` · last human activity ${formatRelativeTime(mem.lastHumanActivityAt)}`}
              {` · revision ${mem.revision}`}
            </p>
            {mem.labels.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {mem.labels.map((l) => (
                  <span key={l} className="rounded bg-muted px-2 py-0.5 text-xs">
                    {l}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {mem.webUrl && (
              <a href={mem.webUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm">
                  Open in GitLab <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </a>
            )}
            <AiActions mem={mem} />
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        {(
          [
            ['overview', 'Overview'],
            ['agents', 'What agents see'],
            ['history', 'History'],
          ] as Array<[Tab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === key ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview mem={mem} />}
      {tab === 'agents' && <AgentView />}
      {tab === 'history' && <History />}
    </div>
  );
}
